import { ChangeEvent, ReactNode, useCallback, useMemo } from "react";
import {
  Control,
  FieldValues,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiControlledSelectBox,
  MiControlledTextField,
  MiIcon,
  MiImagePreview,
  MiImageUpload,
  MiStackedTypography,
} from "@miplatform/components";
import {
  ButtonVariants,
  Colors,
  ISelectOption,
  ITrade,
} from "@miplatform/types";
import { mdiCheck, mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { InspectionAccordion } from "../../InspectionAccordion";
import {
  IInspectionForm,
  IInspectionFormIssue,
} from "../inspectionFormSchemas";

export const IssueFields = <T extends FieldValues = FieldValues>({
  namePrefix,
  control,
  tradeOptions,
  image,
  onImageChange,
  actions,
}: IssueFieldsProps<T>) => {
  const { classes } = useStyles();
  const fieldName = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : field;

  return (
    <>
      <MiControlledTextField
        label="Title"
        name={fieldName("title")}
        formControl={control}
        required
        whiteBackground
      />
      <Box className={classes.row}>
        <MiControlledTextField
          label="Notes"
          name={fieldName("description")}
          formControl={control}
          required
          whiteBackground
          multiline
          rows={3}
        />
        {onImageChange && (
          <MiImageUpload onChange={onImageChange} image={image} />
        )}
      </Box>
      <Box className={classes.row}>
        <MiControlledSelectBox
          name={fieldName("causedByTradeId")}
          label="Caused By"
          required
          formControl={control}
          options={tradeOptions}
          whiteBackground
        />
        <MiControlledSelectBox
          name={fieldName("resolvedByTradeId")}
          label="Resolved By"
          required
          formControl={control}
          options={tradeOptions}
          whiteBackground
        />
        {actions}
      </Box>
    </>
  );
};

export const DraftIssue = ({
  issue,
  index,
  image,
  isOnlyIssue,
  classes,
  control,
  tradeOptions,
  issueIsValid,
  toggleConfirmed,
  handleImageChange,
  remove,
  fields,
}: DraftIssueProps) => (
  <Box className={classes.issueForm} key={fields[index].id}>
    <IssueFields
      namePrefix={`issues.${index}`}
      control={control}
      tradeOptions={tradeOptions}
      image={image}
      onImageChange={(e) => handleImageChange(e, index)}
      actions={
        <>
          <MiButton
            variant={ButtonVariants.Text}
            color="success"
            disabled={!issueIsValid(issue)}
            onClick={() => toggleConfirmed(index)}
          >
            <MiIcon path={mdiCheck} />
          </MiButton>
          <MiButton
            variant={ButtonVariants.Text}
            color="error"
            disabled={isOnlyIssue}
            onClick={() => remove(index)}
          >
            <MiIcon path={mdiTrashCanOutline} />
          </MiButton>
        </>
      }
    />
  </Box>
);

const ConfirmedIssueCard = ({
  issue,
  index,
  image,
  classes,
  tradeNameById,
  toggleConfirmed,
  remove,
  fields,
  theme,
}: ConfirmedIssueProps) => {
  const { title, description, causedByTradeId, resolvedByTradeId } = issue;
  const resolvedByText = tradeNameById[resolvedByTradeId] || resolvedByTradeId;
  const causedByText = tradeNameById[causedByTradeId] || causedByTradeId;

  return (
    <Box key={fields[index].id} className={classes.issueCard}>
      <Box className={classes.issueCardIcon}>
        <MiIcon
          path={mdiCheck}
          color={theme.palette.success.main}
          size={0.67}
        />
      </Box>
      <Box className={classes.issueCardContent}>
        <Box className={classes.row}>
          <Box className={classes.col}>
            <Typography variant="HD1" color="error">
              {title}
            </Typography>
            <Typography variant="BM1" color={theme.palette.grey[600]}>
              {description}
            </Typography>
          </Box>
          {image && (
            <Box className={classes.previewImgContainer}>
              <MiImagePreview url={URL.createObjectURL(image)} />
            </Box>
          )}
        </Box>
        <Box className={classes.row}>
          <MiStackedTypography
            title="Caused By"
            titleFontSize={11}
            description={causedByText}
          />
          <MiStackedTypography
            title="Resolved By"
            titleFontSize={11}
            description={resolvedByText}
          />
          <Box className={classes.buttonControls}>
            <MiButton
              variant={ButtonVariants.Text}
              color={Colors.Grey}
              sx={{
                background: theme.palette.grey[200],
                height: 34,
                width: 34,
              }}
              onClick={() => toggleConfirmed(index)}
            >
              <MiIcon path={mdiPencilOutline} size={0.67} />
            </MiButton>
            <MiButton
              variant={ButtonVariants.Text}
              color="error"
              sx={{
                background: theme.palette.error.light,
                height: 34,
                width: 34,
              }}
              onClick={() => remove(index)}
            >
              <MiIcon path={mdiTrashCanOutline} size={0.67} />
            </MiButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const InspectionIssueForm = ({
  control,
  setValue,
  trades,
}: {
  control: Control<IInspectionForm>;
  setValue: UseFormSetValue<IInspectionForm>;
  trades: ITrade[];
}) => {
  const { theme, classes } = useStyles();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "issues",
  });

  const buildIssueArrays = useCallback(
    (allFields: typeof fields) =>
      allFields.reduce(
        (acc, field, originalIndex) => {
          const bucket = field.confirmed
            ? acc.confirmedIssues
            : acc.unconfirmedIssues;
          bucket.push({ field, index: originalIndex });
          return acc;
        },
        {
          confirmedIssues: [] as {
            field: (typeof fields)[number];
            index: number;
          }[],
          unconfirmedIssues: [] as {
            field: (typeof fields)[number];
            index: number;
          }[],
        }
      ),
    []
  );

  const { confirmedIssues, unconfirmedIssues } = useMemo(
    () => buildIssueArrays(fields),
    [fields, buildIssueArrays]
  );

  const watchedIssues = useWatch({ control, name: "issues" });

  const toggleConfirmed = useCallback(
    (index: number) => {
      const current = watchedIssues?.[index] ?? fields[index];
      update(index, { ...current, confirmed: !current?.confirmed });
    },
    [watchedIssues, fields, update]
  );

  const tradeOptions = useMemo(
    () => trades.map((t) => ({ label: t.name, value: t.id })),
    [trades]
  );

  const tradeNameById = useMemo(() => {
    const map: Record<string, string> = {};
    trades.forEach((t) => {
      map[t.id] = t.name;
    });
    return map;
  }, [trades]);

  const handleImageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | null, index: number) => {
      const { files } = e?.target ?? {};
      const uploadedFile = files ? files[0] : null;
      setValue(`issues.${index}.files`, uploadedFile ? [uploadedFile] : []);
    },
    [setValue]
  );

  const issueIsValid = useCallback((issue?: IInspectionFormIssue) => {
    if (!issue) return false;
    return (
      !!issue.title?.trim() &&
      !!issue.description?.trim() &&
      !!issue.causedByTradeId &&
      !!issue.resolvedByTradeId
    );
  }, []);

  const NEW_ISSUE_TEMPLATE = useMemo(
    () => ({
      description: "",
      title: "",
      causedByTradeId: "",
      resolvedByTradeId: "",
      files: [],
      confirmed: false,
    }),
    []
  );

  const appendNewIssue = useCallback(
    () => append(NEW_ISSUE_TEMPLATE),
    [append, NEW_ISSUE_TEMPLATE]
  );

  return (
    <>
      <Box className={classes.issuesContainer}>
        {unconfirmedIssues.map(({ field: fieldSet, index }) => {
          const issue = watchedIssues?.[index] ?? fieldSet;
          const image = issue?.files?.[0];
          return (
            <DraftIssue
              key={fields[index].id}
              issue={issue}
              index={index}
              image={image}
              isOnlyIssue={fields.length === 1}
              classes={classes}
              control={control}
              tradeOptions={tradeOptions}
              issueIsValid={issueIsValid}
              toggleConfirmed={toggleConfirmed}
              handleImageChange={handleImageChange}
              remove={remove}
              fields={fields}
            />
          );
        })}
        <InspectionAccordion
          title="Confirmed Issues"
          count={confirmedIssues.length}
          backgroundColor={theme.palette.error.main}
        >
          <Box className={classes.confirmedIssues}>
            {confirmedIssues.map(({ field: fieldSet, index }) => {
              const issue = watchedIssues?.[index] ?? fieldSet;
              const image = issue?.files?.[0];
              return (
                <ConfirmedIssueCard
                  key={fields[index].id}
                  issue={issue}
                  index={index}
                  image={image}
                  classes={classes}
                  tradeNameById={tradeNameById}
                  toggleConfirmed={toggleConfirmed}
                  remove={remove}
                  fields={fields}
                  theme={theme}
                />
              );
            })}
          </Box>
        </InspectionAccordion>
      </Box>
      <MiButton
        title="Add Additional Issue"
        variant={ButtonVariants.Outlined}
        className={classes.addNotesButton}
        onClick={appendNewIssue}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  issuesContainer: {
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    flex: 1,
  },
  issueCard: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.common.white,
    borderRadius: 4,
  },
  issueForm: {
    display: "flex",
    flex: 1,
    width: "100%",
    gap: theme.spacing(2),
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  confirmedIssues: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  issueInputs: {
    display: "flex",
    flex: 1,
    gap: theme.spacing(2),
    flexDirection: "column",
  },
  issueCardContent: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  row: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  col: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  addNotesButton: {
    justifySelf: "end",
  },
  issueCardIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    borderLeft: `4px solid ${theme.palette.success.dark}`,
    background: theme.palette.success.light,
  },
  buttonControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  previewImgContainer: {
    width: 40,
    height: 40,
  },
}));

type DraftIssueProps = {
  issue: IInspectionFormIssue;
  index: number;
  image?: File;
  isOnlyIssue: boolean;
  classes: ReturnType<typeof useStyles>["classes"];
  control: Control<IInspectionForm>;
  tradeOptions: { label: string; value: string }[];
  issueIsValid: (issue?: IInspectionFormIssue) => boolean;
  toggleConfirmed: (index: number) => void;
  handleImageChange: (
    e: ChangeEvent<HTMLInputElement> | null,
    index: number
  ) => void;
  remove: (index?: number | number[]) => void;
  fields: { id: string }[];
};

type ConfirmedIssueProps = {
  issue: IInspectionFormIssue;
  index: number;
  image?: File;
  classes: ReturnType<typeof useStyles>["classes"];
  tradeNameById: Record<string, string>;
  toggleConfirmed: (index: number) => void;
  remove: (index?: number | number[]) => void;
  fields: { id: string }[];
  theme: ReturnType<typeof useStyles>["theme"];
};

type IssueFieldsProps<T extends FieldValues = FieldValues> = {
  namePrefix?: string;
  control: Control<T>;
  tradeOptions: ISelectOption[];
  image?: File;
  onImageChange?: (e: ChangeEvent<HTMLInputElement> | null) => void;
  actions?: ReactNode;
};
